"use client";

import { PageStateProvider } from "@2u-landing-pages/providers";
import { StickyElement } from "@2u-landing-pages/templates";
import {
  TypeCategoryWithoutUnresolvableLinksResponse,
  TypeCategory_variationWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import { ContentfulLivePreviewProvider, useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { ErrorBoundary, logCustomAttributes, logEvent } from "@red-digital/bricks";
import resolveResponse from "contentful-resolve-response";
import React, { useEffect, useRef, useState } from "react";
import { InlineDrawerForm } from "./InlineDrawerForm";
import { PreviewMode } from "./PreviewMode";
import { Sections } from "./Sections";

interface PageProps {
  category: TypeCategoryWithoutUnresolvableLinksResponse;
  preview?: boolean;
  internal?: boolean;
  writeKey?: string;
  params?: {
    lob: string;
    slug: string;
  };
}

const Page: React.FC<PageProps> = ({ category, preview, internal, params }) => {
  const [variation, setVariation] = useState<TypeCategory_variationWithoutUnresolvableLinksResponse | undefined>(
    category?.fields?.base_variation
  );
  const [isLoading, setIsLoading] = useState<boolean>(!preview);
  const decisionRendered = useRef(false);
  const manualDecisionTriggered = useRef(false);

  const livePreviewVariation = useContentfulLiveUpdates(
    variation
  ) as TypeCategory_variationWithoutUnresolvableLinksResponse;

  const activeVariation = preview ? livePreviewVariation : variation;

  const sections = activeVariation?.fields?.sections || [];
  const stickyElement = activeVariation?.fields?.sticky_element || null;

  useEffect(() => {
    if (preview) return;

    logCustomAttributes([
      { name: "Release", value: process.env.RELEASE_VERSION || "" },
      { name: "LOB", value: params?.lob || "" },
    ]);

    EventClient.elementLoaded({
      webElement: {
        elementType: "Contentful Multi Program",
        name: activeVariation?.fields?.name || "",
        htmlId: activeVariation?.sys?.id || "",
        location: "Contentful Multi Program",
      },
    });

    if (window.cohesion) {
      window.cohesion("monarch:done", function (err: object | null, result: any) {
        // Prevents monarch:done from being called multiple times
        if (decisionRendered.current) return;

        if (err) {
          console.log("Monarch Error", err);
          setIsLoading(false);

          EventClient.tempMonarchDecision({
            webElement: {
              location: "Monarch Error",
              name: activeVariation?.fields?.name || "",
              htmlId: activeVariation?.sys?.id || "",
              text: JSON.stringify(err),
            },
          });

          logEvent("monarch_error", {
            error: JSON.stringify(err),
          });

          return;
        }

        let resolvedContent: TypeCategory_variationWithoutUnresolvableLinksResponse | undefined;
        const monarchVariation = params?.slug
          ? (result?.source?.[params.slug]?.value as TypeCategory_variationWithoutUnresolvableLinksResponse)
          : undefined;

        if (monarchVariation?.sys) {
          resolvedContent = resolveResponse(monarchVariation)?.[0];
          if (resolvedContent) {
            setVariation(resolvedContent);

            logEvent("monarch_decision", {
              variation: resolvedContent?.fields?.name || "",
              sysId: resolvedContent?.sys?.id || "",
            });
          }
        }

        EventClient.tempMonarchDecision({
          webElement: {
            location: "Decision Rendered",
            name: resolvedContent?.fields?.name || activeVariation?.fields?.name || "",
            htmlId: resolvedContent?.sys?.id || activeVariation?.sys?.id || "",
          },
        });

        // Prevents monarch:done from being called multiple times
        decisionRendered.current = true;
        setIsLoading(false);
      });

      window.cohesion("ready", () => {
        const sessionId = window?._Cohesion?.sessionId || "";
        logCustomAttributes([{ name: "ClientSessionId", value: sessionId || "" }]);
        if (window?.newrelic) {
          window?.newrelic?.setUserId(sessionId);
        }
      });

      window.cohesion("consent:changed", function (groups) {
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));
        if (consentCaptured) {
          logEvent("consent_captured", {
            consentCaptured,
            groups: groups.join(", "),
          });
        }
      });

      window.cohesion("error", () => {
        EventClient.tempMonarchDecision({
          webElement: {
            location: "Cohesion Error",
            name: activeVariation?.fields?.name || "",
            htmlId: activeVariation?.sys?.id || "",
          },
        });
        setIsLoading(false);
      });

      window.cohesion("tagular:eventSucceeded", (event: any) => {
        if (window.monarch && event?.event === "core.redventures.usertracking.ConsentCaptured.v1") {
          if (manualDecisionTriggered.current || !event?.properties?.webContext?.cohesionConsent) return;
          // window.monarch(
          //   "rule",
          //   school.fields.slug,
          //   {
          //     application: "rv_creative",
          //     callType: "manual",
          //     experienceServed: variationName.current,
          //   },
          //   {
          //     writeKey,
          //   },
          //   () => null
          // );
          manualDecisionTriggered.current = true;
        }
      });
    }
  }, [preview]);

  // @TODO: Remove this once the lob field is available
  // Extract LOB from category name since direct lob field is unavailable
  const category_name = category?.fields?.base_variation?.fields?.name;
  const lob = (() => {
    if (category?.fields?.lob) return category.fields.lob;

    if (category_name) {
      if (category_name.includes("EE")) return "execed";
      if (category_name.includes("DG")) return "degree";
    }

    return undefined;
  })();

  return (
    <ContentfulLivePreviewProvider locale="en-US" enableInspectorMode={true} enableLiveUpdates={true}>
      <style>
        {`
          :root {
            --action-button-primary-active: var(--primary-6);
            --action-button-primary-default: var(--primary-6);
            --action-button-primary-focus: var(--primary-6);
            --action-button-primary-hover: var(--primary-6);
            --action-button-primary-light-active: var(--primary-6);
            --action-button-primary-light-default: var(--primary-6);
            --action-button-primary-light-focus: var(--primary-6);
            --action-button-primary-light-hover: var(--primary-6);

            --action-button-secondary-active: var(--primary-6);
            --action-button-secondary-default: var(--primary-6);
            --action-button-secondary-focus: var(--primary-6);
            --action-button-secondary-hover: var(--primary-6);
            --action-button-secondary-light-active: var(--primary-6);
            --action-button-secondary-light-default: var(--primary-6);
            --action-button-secondary-light-focus: var(--primary-6);
            --action-button-secondary-light-hover: var(--primary-6);
          }

          .page {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
          }
        `}
      </style>
      <div className="page" style={{ opacity: isLoading ? 0 : 1 }}>
        <PageStateProvider>
          <ErrorBoundary title="Uh-oh! An Unexpected Issue Occurred" production={process.env.NEXT_ENV === "production"}>
            {preview && <PreviewMode internal={internal} />}
            <Sections sections={sections} lob={lob} category={params?.slug} />
            <InlineDrawerForm showLargeLogo={true} />
            {stickyElement && <StickyElement id={stickyElement.sys.id} {...stickyElement.fields} />}
          </ErrorBoundary>
        </PageStateProvider>
      </div>
    </ContentfulLivePreviewProvider>
  );
};

export default Page;
